import { DashboardSidebarHtml } from './skins/dashboard-sidebar.ts';
import { navigateTo } from "../framework/router.ts";
import Utils from "../framework/utils.ts";
import { authContext } from "../context/AuthContext.ts";
import { globalState } from "../framework/globalState.ts";
import { initMqtt, disconnectMqtt } from "../services/mqttClient.ts";

export default function DashboardSidebar() {
  const sidebarContainer = document.querySelector('.sidebar') as HTMLElement | null;
  if (!sidebarContainer) return;

  injectHTML();
  bindDelegatedEventsOnce();
  syncRealtimeToggleUi();
  syncThemeToggleUi();
  highlightActiveRoute();

  if (!(window as any).__vetraSidebarPopstateBound) {
    window.addEventListener('popstate', () => highlightActiveRoute());
    (window as any).__vetraSidebarPopstateBound = true;
  }

  return sidebarContainer;

  function injectHTML() {
    if (!sidebarContainer.dataset.initialized) {
      sidebarContainer.innerHTML = new DashboardSidebarHtml().sidebarskin();
      sidebarContainer.dataset.initialized = 'true';

      const isCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
      sidebarContainer.dataset.collapsed = isCollapsed ? 'true' : 'false';
    }
  }

  function highlightActiveRoute(path = window.location.pathname) {
    sidebarContainer.querySelectorAll<HTMLElement>('[data-path]').forEach((el) => {
      const route = String(el.dataset.path || '').replace(/\/$/, '');
      const current = path.replace(/\/$/, '');
      el.classList.toggle('active', route === current);
    });
  }

  function bindDelegatedEventsOnce() {
    if (sidebarContainer.dataset.listenersBound === 'true') return;
    sidebarContainer.dataset.listenersBound = 'true';

    sidebarContainer.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const logo = target.closest('.navbar-brand');
      if (logo) {
        e.preventDefault();
        navigateTo('../overview');
        return;
      }

      const sidebarToggle = target.closest('#sidebarToggle');
      if (sidebarToggle) {
        e.preventDefault();
        const isCollapsed = sidebarContainer.dataset.collapsed === 'true';
        const newState = isCollapsed ? 'false' : 'true';
        sidebarContainer.dataset.collapsed = newState;
        localStorage.setItem('sidebarCollapsed', newState);
        return;
      }

      const dropdownToggle = target.closest('.dropdown-toggle');
      if (dropdownToggle) {
        e.preventDefault();
        e.stopPropagation();
        const menuItem = dropdownToggle.closest('.has-submenu') as HTMLElement | null;
        menuItem?.classList.toggle('open');
        return;
      }

      const themeToggle = target.closest('#themeToggle');
      if (themeToggle) {
        const currentTheme = Utils.getCurrentTheme();
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        Utils.setTheme(newTheme);
        syncThemeToggleUi();
        window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: newTheme } }));
        return;
      }

      const logoutButton = target.closest('#logout');
      if (logoutButton) {
        authContext.logout();
        navigateTo('/login');
        return;
      }

      const mqttToggleBtn = target.closest('#realtimeToggle') as HTMLElement | null;
      if (mqttToggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        await handleRealtimeToggle(mqttToggleBtn);
        return;
      }

      const menuLabel = target.closest('.has-submenu .menu-label') as HTMLElement | null;
      if (menuLabel) {
        const menuItem = menuLabel.closest('.has-submenu') as HTMLElement | null;
        const path = menuItem?.dataset.path;
        if (path) {
          navigateTo(path);
          highlightActiveRoute(path);
        }
        return;
      }

      const routeItem = target.closest('li[data-path]') as HTMLElement | null;
      if (!routeItem) return;
      if (routeItem.classList.contains('has-submenu')) return;

      e.preventDefault();
      e.stopPropagation();
      const path = routeItem.dataset.path;
      if (path) {
        navigateTo(path);
        highlightActiveRoute(path);
        const parentSubmenu = routeItem.closest('.submenu');
        const parentMenuItem = parentSubmenu?.previousElementSibling as HTMLElement | null;
        parentMenuItem?.classList.remove('open');
      }
    });

    sidebarContainer.addEventListener('keydown', async (e) => {
      const keyboardEvent = e as KeyboardEvent;
      if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;

      const target = keyboardEvent.target as HTMLElement | null;
      const toggle = target?.closest('#realtimeToggle') as HTMLElement | null;
      if (!toggle) return;

      keyboardEvent.preventDefault();
      await handleRealtimeToggle(toggle);
    });
  }

  function syncRealtimeToggleUi() {
    const MQTT_TOGGLE_KEY = "vetra.mqttLiveEnabled";
    const toggleBtn = sidebarContainer.querySelector('#realtimeToggle') as HTMLElement | null;
    const iconWrap = sidebarContainer.querySelector('#mqttRealtimeIconWrap') as HTMLElement | null;
    if (!toggleBtn || !iconWrap) return;

    const isEnabled = () => localStorage.getItem(MQTT_TOGGLE_KEY) !== 'false';

    const enabled = isEnabled();
    iconWrap.classList.toggle('is-on', enabled);
    iconWrap.classList.toggle('is-off', !enabled);
    toggleBtn.setAttribute('aria-pressed', String(enabled));
    toggleBtn.title = enabled ? 'Disable MQTT real time' : 'Enable MQTT real time';
  }

  async function handleRealtimeToggle(toggleBtn: HTMLElement) {
    const MQTT_TOGGLE_KEY = "vetra.mqttLiveEnabled";
    const isEnabled = () => localStorage.getItem(MQTT_TOGGLE_KEY) !== 'false';

    toggleBtn.classList.add('is-busy');
    toggleBtn.setAttribute('aria-disabled', 'true');
    try {
      const nextEnabled = !isEnabled();
      localStorage.setItem(MQTT_TOGGLE_KEY, String(nextEnabled));

      if (nextEnabled) {
        const token = (globalState.token || localStorage.getItem('token')) as string | null;
        if (token) await initMqtt(token);
      } else {
        disconnectMqtt();
      }
    } catch (err) {
      console.warn('[DashboardSidebar] Failed to toggle realtime MQTT', err);
    } finally {
      syncRealtimeToggleUi();
      toggleBtn.classList.remove('is-busy');
      toggleBtn.removeAttribute('aria-disabled');
    }
  }

  function syncThemeToggleUi() {
    const themeToggle = sidebarContainer.querySelector('#themeToggle') as HTMLElement | null;
    if (!themeToggle) return;

    const updateThemeIcon = (theme: string) => {
      const icon = theme === 'light'
        ? '<span class="iconify" data-icon="si:toggle-off-fill" data-inline="false"></span> <span class="menu-label">Theme</span>'
        : '<span class="iconify" data-icon="ri:toggle-line" data-inline="false"></span> <span class="menu-label">Theme</span>';
      themeToggle.innerHTML = icon;
    };

    const initialTheme = Utils.getCurrentTheme();
    Utils.setTheme(initialTheme);
    updateThemeIcon(initialTheme);
  }
}
